/**
 * "Make it yours" panel (Spec 11): light/dark + the five color roles the client
 * approved — Primary, Secondary, Headings, Paragraph, Numbers — plus a one-click
 * "Use my brand colors". Each role: preset swatches + a color wheel + hex.
 * Headings/Paragraph/Numbers include an "Auto" (follow the theme) option.
 */

import { useEffect, useRef, useState } from "react";
import { useTheme, type ColorRole } from "../lib/theme.js";

const HEX_RE = /^#[0-9a-fA-F]{6}$/;
/** Accept "1d4ed8" or "#1d4ed8"; return a normalized "#rrggbb" or null. */
function normalizeHex(s: string): string | null {
  const t = s.trim();
  const withHash = t.startsWith("#") ? t : `#${t}`;
  return HEX_RE.test(withHash) ? withHash.toLowerCase() : null;
}

const PALETTE = [
  "#ef4444", "#f97316", "#f59e0b", "#eab308", "#84cc16", "#22c55e", "#15b8a6",
  "#06b6d4", "#7dd3fc", "#60a5fa", "#3b82f6", "#1d4ed8", "#6366f1", "#8b5cf6", "#ec4899", "#64748b",
];
const HEADINGS = { dark: ["#ffffff", "#c3c8d4", "#9197ab"], light: ["#0b0d12", "#3a3f4b", "#6b7280"] };
const PARAGRAPH = { dark: ["#a6acbd", "#c3c8d4", "#7e859a"], light: ["#525a6b", "#3a3f4b", "#7b8294"] };

export function CustomizePanel(): React.ReactElement {
  const { mode, setMode, colors, setColor, useBrandColors } = useTheme();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close on Escape or a click outside. The open popover overlaps the page's
  // other "Customize" (edit layout) button, so without easy dismissal a misclick
  // lands on "Use my brand colors" and recolors the app. Both exits must work.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    const onDown = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open]);

  const Swatch = ({ role, hex, label }: { role: ColorRole; hex: string; label?: string }): React.ReactElement => (
    <button
      title={label ?? hex}
      onClick={() => setColor(role, hex)}
      style={{
        width: 22,
        height: 22,
        borderRadius: "50%",
        cursor: "pointer",
        border: "2px solid transparent",
        background:
          hex === "auto" ? "linear-gradient(135deg,#fff 49%,#111 51%)" : hex,
        ...(colors[role].toLowerCase() === hex.toLowerCase()
          ? { boxShadow: "0 0 0 2px var(--surface), 0 0 0 4px var(--text)" }
          : {}),
      }}
    />
  );

  const Row = ({
    role,
    presets,
    withAuto,
  }: {
    role: ColorRole;
    presets: string[];
    withAuto?: boolean;
  }): React.ReactElement => {
    const current = colors[role];
    return (
      <>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {withAuto && <Swatch role={role} hex="auto" label="Auto (follow theme)" />}
          {presets.map((h) => (
            <Swatch key={h} role={role} hex={h} />
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
          <HexInput value={current} onCommit={(hex) => setColor(role, hex)} />
          <input
            type="color"
            value={current === "auto" ? "#888888" : current}
            onChange={(e) => setColor(role, e.target.value)}
            style={wheelStyle}
          />
        </div>
      </>
    );
  };

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <button onClick={() => setOpen((o) => !o)} style={btnStyle}>
        🎨 Customize design
      </button>
      {open && (
        <div style={popStyle}>
          <button onClick={useBrandColors} style={{ ...btnStyle, width: "100%", justifyContent: "center", marginBottom: 8 }}>
            ✨ Use my brand colors
          </button>
          <H>Mode</H>
          <div style={segStyle}>
            {(["dark", "light"] as const).map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                style={{ ...segBtn, ...(mode === m ? segOn : {}), flex: 1 }}
              >
                {m === "dark" ? "Dark" : "Light"}
              </button>
            ))}
          </div>
          <H>Primary color</H>
          <Row role="accent" presets={PALETTE} />
          <H>Secondary color</H>
          <Row role="accent2" presets={PALETTE} />
          <H>Headings</H>
          <Row role="text" presets={HEADINGS[mode]} withAuto />
          <H sub="copy, captions, labels">Paragraph</H>
          <Row role="body" presets={PARAGRAPH[mode]} withAuto />
          <H sub="the metric values">Numbers</H>
          <Row role="num" presets={PALETTE} withAuto />
        </div>
      )}
    </div>
  );
}

/**
 * Hex field with a local draft. Applying a color writes a CSS variable live, so
 * a half-typed value ("1", "#1d4") must NOT be applied — an invalid var blanks
 * everything that uses it (e.g. fill: var(--accent) → black). We only commit a
 * fully valid 6-digit hex; junk reverts to the live value on blur.
 */
function HexInput({ value, onCommit }: { value: string; onCommit: (hex: string) => void }): React.ReactElement {
  const [draft, setDraft] = useState(value);
  useEffect(() => {
    setDraft(value);
  }, [value]);
  const invalid = draft.trim() !== "" && value !== "auto" && normalizeHex(draft) === null;
  const commit = (): void => {
    const hex = normalizeHex(draft);
    if (hex) onCommit(hex);
    else setDraft(value);
  };
  return (
    <input
      value={draft}
      spellCheck={false}
      onChange={(e) => {
        setDraft(e.target.value);
        const hex = normalizeHex(e.target.value);
        if (hex) onCommit(hex);
      }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit();
      }}
      style={{ ...hexStyle, ...(invalid ? { borderColor: "var(--bad)" } : {}) }}
    />
  );
}

function H({ children, sub }: { children: React.ReactNode; sub?: string }): React.ReactElement {
  return (
    <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--faint)", margin: "14px 0 8px", fontWeight: 600 }}>
      {children}
      {sub && <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 400 }}> · {sub}</span>}
    </div>
  );
}

const btnStyle: React.CSSProperties = {
  display: "inline-flex",
  gap: 7,
  alignItems: "center",
  border: "1px solid var(--border)",
  background: "var(--surface-2)",
  color: "var(--text)",
  borderRadius: "var(--r-sm)",
  padding: "7px 13px",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
};
const popStyle: React.CSSProperties = {
  position: "absolute",
  right: 0,
  top: 42,
  width: 300,
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: "var(--r-card)",
  boxShadow: "var(--shadow-pop)",
  padding: 16,
  zIndex: 60,
};
const segStyle: React.CSSProperties = {
  display: "inline-flex",
  width: "100%",
  background: "var(--surface-2)",
  border: "1px solid var(--border)",
  borderRadius: 9,
  padding: 2,
};
const segBtn: React.CSSProperties = {
  border: "none",
  background: "transparent",
  color: "var(--body)",
  fontSize: 12.5,
  fontWeight: 600,
  padding: "6px 12px",
  borderRadius: 7,
  cursor: "pointer",
};
const segOn: React.CSSProperties = { background: "var(--accent)", color: "var(--accent-contrast)" };
const hexStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  border: "1px solid var(--border)",
  background: "var(--surface-2)",
  color: "var(--text)",
  borderRadius: 7,
  padding: "6px 8px",
  fontFamily: "var(--font-mono)",
  fontSize: 12,
};
const wheelStyle: React.CSSProperties = {
  width: 36,
  height: 32,
  padding: 0,
  border: "1px solid var(--border)",
  borderRadius: 7,
  background: "var(--surface-2)",
  cursor: "pointer",
};
